import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';

/**
 * Organization SMS Template Service
 * 
 * Provides organization-specific SMS templates with fallback to platform defaults.
 * Ensures backward compatibility and zero downtime during migration.
 */

// PLATFORM DEFAULT TEMPLATES - These are the current hardcoded templates
// Used as fallback when no org-specific templates exist
const PLATFORM_DEFAULT_TEMPLATES = {
  emergency: [
    {
      id: 'emergency_evacuation',
      name: 'Evacuation Notice',
      message: 'URGENT EVACUATION: Immediate evacuation required at {site_name}. Follow your emergency procedures. Confirm evacuation completion by replying SAFE. Time: {timestamp}',
      category: 'emergency',
      variables: ['site_name', 'timestamp'],
      isDefault: true
    },
    {
      id: 'emergency_safety',
      name: 'Safety Check',
      message: 'This is a site security/safety check from {organisation_name} Crisis Management. Please confirm your site\'s operational status:\n1 = Active\n2 = Degraded\n3 = Evacuating\n4 = Evacuated\n5 = Closed and Secure',
      category: 'emergency',
      variables: ['organisation_name'],
      isDefault: true
    }
  ],
  operational: [
    {
      id: 'ops_status_request',
      name: 'Status Request',
      message: 'Hi {manager_name}, please provide current operational status for {site_name}. Include staff count, any issues, and expected closure time if applicable.',
      category: 'operational',
      variables: ['manager_name', 'site_name'],
      isDefault: true
    },
    {
      id: 'ops_closure_notice',
      name: 'Site Closure',
      message: 'Notice: {site_name} will be temporarily closed due to current situation. Please secure the site and ensure all staff are safely evacuated.',
      category: 'operational',
      variables: ['site_name'],
      isDefault: true
    }
  ],
  general: [
    {
      id: 'general_alert',
      name: 'General Alert',
      message: 'ALERT: This is an urgent notification regarding your site. Please respond immediately to confirm your safety and site status.',
      category: 'general',
      variables: [],
      isDefault: true
    },
    {
      id: 'incident_notification',
      name: 'Incident Notification',
      message: 'Incident notification: An incident has been reported in your area. Please review your site security and report any unusual activity immediately.',
      category: 'general',
      variables: [],
      isDefault: true
    }
  ]
};

/**
 * Get all available SMS templates for an organization
 * Returns org-specific templates if they exist, otherwise falls back to platform defaults
 * 
 * @param {Object} db - Firestore database instance
 * @param {string} orgId - Organization ID
 * @returns {Promise<Object>} Templates organized by category
 */
export const getOrgSmsTemplates = async (db, orgId) => {
  try {
    if (!db || !orgId) {
      console.warn('🔄 SMS Templates: Missing db or orgId, returning platform defaults');
      return PLATFORM_DEFAULT_TEMPLATES;
    }

    // Query org-specific templates
    const templatesRef = collection(db, 'c3_org_sms_templates');
    const q = query(
      templatesRef,
      where('orgId', '==', orgId),
      where('isActive', '==', true)
    );
    
    const snapshot = await getDocs(q);
    const orgTemplates = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      isOrgSpecific: true
    }));

    console.log(`📱 SMS Templates: Found ${orgTemplates.length} org-specific templates for ${orgId}`);

    // If no org templates exist, return platform defaults
    if (orgTemplates.length === 0) {
      console.log('📱 SMS Templates: No org templates found, using platform defaults');
      return PLATFORM_DEFAULT_TEMPLATES;
    }

    // Organize org templates by category
    const organizedTemplates = {
      emergency: [],
      operational: [],
      general: []
    };

    orgTemplates.forEach(template => {
      const category = template.category || 'general';
      if (organizedTemplates[category]) {
        organizedTemplates[category].push(template);
      } else {
        organizedTemplates.general.push(template);
      }
    });

    // If any category is empty, fill with platform defaults
    Object.keys(PLATFORM_DEFAULT_TEMPLATES).forEach(category => {
      if (organizedTemplates[category].length === 0) {
        console.log(`📱 SMS Templates: No org templates for ${category}, using platform defaults`);
        organizedTemplates[category] = PLATFORM_DEFAULT_TEMPLATES[category];
      }
    });

    return organizedTemplates;

  } catch (error) {
    console.error('❌ SMS Templates: Error fetching org templates, falling back to defaults:', error);
    return PLATFORM_DEFAULT_TEMPLATES;
  }
};

/**
 * Get SMS templates in the legacy format for backward compatibility
 * Returns a flat array of templates as expected by existing components
 * 
 * @param {Object} db - Firestore database instance
 * @param {string} orgId - Organization ID
 * @param {string} format - 'advanced' | 'site' | 'individual' | 'comms'
 * @returns {Promise<Array>} Flat array of templates
 */
export const getSmsTemplatesLegacyFormat = async (db, orgId, format = 'advanced') => {
  try {
    const organizedTemplates = await getOrgSmsTemplates(db, orgId);
    
    // Flatten all templates into a single array
    const allTemplates = [
      ...organizedTemplates.emergency,
      ...organizedTemplates.operational,
      ...organizedTemplates.general
    ];

    // Format based on component needs
    switch (format) {
      case 'site':
        // Format for SiteListPopup component
        return allTemplates.slice(0, 4).map(template => ({
          id: template.id,
          name: template.name,
          message: template.message
        }));

      case 'individual':
        // Format for MapCore individual SMS
        return allTemplates.slice(0, 3).map(template => ({
          id: template.id,
          name: template.name,
          message: template.message
        }));

      case 'comms':
        // Format for CommsTab
        return allTemplates.map(template => ({
          id: template.id,
          name: template.name,
          subject: `SMS: ${template.name}`,
          body: template.message,
          type: 'sms',
          category: template.category || 'general'
        }));

      case 'advanced':
      default:
        // Format for AdvancedSMSComposer (organized by category)
        return {
          emergency: {
            name: 'Emergency Response',
            templates: organizedTemplates.emergency.map(template => ({
              id: template.id,
              name: template.name,
              message: template.message,
              category: 'emergency',
              variables: template.variables || []
            }))
          },
          operational: {
            name: 'Operational Updates', 
            templates: organizedTemplates.operational.map(template => ({
              id: template.id,
              name: template.name,
              message: template.message,
              category: 'operational',
              variables: template.variables || []
            }))
          }
        };
    }

  } catch (error) {
    console.error('❌ SMS Templates: Error in legacy format conversion:', error);
    
    // Return safe defaults based on format
    if (format === 'advanced') {
      return PLATFORM_DEFAULT_TEMPLATES;
    }
    
    // Return flat array for other formats
    const allDefaults = [
      ...PLATFORM_DEFAULT_TEMPLATES.emergency,
      ...PLATFORM_DEFAULT_TEMPLATES.operational,
      ...PLATFORM_DEFAULT_TEMPLATES.general
    ];
    
    return format === 'site' ? allDefaults.slice(0, 4) :
           format === 'individual' ? allDefaults.slice(0, 3) :
           allDefaults;
  }
};

/**
 * Create a new organization SMS template
 * 
 * @param {Object} db - Firestore database instance
 * @param {string} orgId - Organization ID
 * @param {Object} templateData - Template data
 * @param {string} userId - User ID creating the template
 * @returns {Promise<string>} Created template ID
 */
export const createOrgSmsTemplate = async (db, orgId, templateData, userId) => {
  try {
    const templatesRef = collection(db, 'c3_org_sms_templates');
    
    const template = {
      orgId,
      name: templateData.name || 'Untitled Template',
      message: templateData.message || '',
      category: templateData.category || 'general',
      variables: extractVariables(templateData.message || ''),
      isActive: true,
      createdBy: userId,
      createdAt: serverTimestamp(),
      updatedBy: userId,
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(templatesRef, template);
    console.log(`✅ SMS Template: Created template ${docRef.id} for org ${orgId}`);
    
    return docRef.id;

  } catch (error) {
    console.error('❌ SMS Template: Error creating template:', error);
    throw error;
  }
};

/**
 * Update an organization SMS template
 * 
 * @param {Object} db - Firestore database instance
 * @param {string} templateId - Template ID to update
 * @param {Object} updates - Updates to apply
 * @param {string} userId - User ID making the update
 * @returns {Promise<void>}
 */
export const updateOrgSmsTemplate = async (db, templateId, updates, userId) => {
  try {
    const templateRef = doc(db, 'c3_org_sms_templates', templateId);
    
    const updateData = {
      ...updates,
      variables: updates.message ? extractVariables(updates.message) : undefined,
      updatedBy: userId,
      updatedAt: serverTimestamp()
    };

    // Remove undefined values
    Object.keys(updateData).forEach(key => {
      if (updateData[key] === undefined) {
        delete updateData[key];
      }
    });

    await updateDoc(templateRef, updateData);
    console.log(`✅ SMS Template: Updated template ${templateId}`);

  } catch (error) {
    console.error('❌ SMS Template: Error updating template:', error);
    throw error;
  }
};

/**
 * Delete an organization SMS template (soft delete)
 * 
 * @param {Object} db - Firestore database instance
 * @param {string} templateId - Template ID to delete
 * @param {string} userId - User ID performing the deletion
 * @returns {Promise<void>}
 */
export const deleteOrgSmsTemplate = async (db, templateId, userId) => {
  try {
    const templateRef = doc(db, 'c3_org_sms_templates', templateId);
    
    await updateDoc(templateRef, {
      isActive: false,
      deletedBy: userId,
      deletedAt: serverTimestamp(),
      updatedBy: userId,
      updatedAt: serverTimestamp()
    });

    console.log(`✅ SMS Template: Soft deleted template ${templateId}`);

  } catch (error) {
    console.error('❌ SMS Template: Error deleting template:', error);
    throw error;
  }
};

/**
 * Extract variables from a template message
 * Finds patterns like {variable_name}
 * 
 * @param {string} message - Template message
 * @returns {Array<string>} Array of variable names
 */
export const extractVariables = (message) => {
  if (!message) return [];
  
  const matches = message.match(/{([^}]+)}/g);
  if (!matches) return [];
  
  return matches.map(match => match.slice(1, -1)); // Remove { and }
};

/**
 * Validate a template message
 * 
 * @param {string} message - Template message to validate
 * @returns {Object} Validation result
 */
export const validateTemplateMessage = (message) => {
  const errors = [];
  const warnings = [];
  
  if (!message || message.trim().length === 0) {
    errors.push('Message cannot be empty');
  }
  
  if (message && message.length > 1600) {
    errors.push('Message is too long (max 1600 characters for SMS concatenation)');
  }
  
  if (message && message.length > 160) {
    warnings.push(`Message will be sent as ${Math.ceil(message.length / 160)} SMS segments`);
  }
  
  const variables = extractVariables(message);
  const invalidVariables = variables.filter(v => !isValidVariableName(v));
  
  if (invalidVariables.length > 0) {
    warnings.push(`Unknown variables: ${invalidVariables.join(', ')}`);
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    characterCount: message ? message.length : 0,
    segmentCount: message ? Math.ceil(message.length / 160) : 0,
    variables
  };
};

/**
 * Check if a variable name is valid/recognized
 * 
 * @param {string} variableName - Variable name to check
 * @returns {boolean} Whether the variable is recognized
 */
const isValidVariableName = (variableName) => {
  const knownVariables = [
    'organisation_name',
    'organization_name', 
    'site_name',
    'manager_name',
    'incident_title',
    'location',
    'timestamp',
    'current_time',
    'current_date',
    'status',
    'severity'
  ];
  
  return knownVariables.includes(variableName);
};

// Export default object with all functions and constants
export default {
  getOrgSmsTemplates,
  getSmsTemplatesLegacyFormat,
  createOrgSmsTemplate,
  updateOrgSmsTemplate,
  deleteOrgSmsTemplate,
  extractVariables,
  validateTemplateMessage,
  PLATFORM_DEFAULT_TEMPLATES
};

// Export individual constants and functions for named imports
export { PLATFORM_DEFAULT_TEMPLATES }; 